import AreaChartGraph from "@/Graphs/AreaChartGraph";
import usePartnerCertificates from "@/PartnerPages/Certificates/hooks/usePartnerCertificates";
import { MONTHS } from "@/PartnerPages/Home/dashboard.constant";
import ChartContainer from "@/UI/Containers/ChartContainer";
import { CurrencyRupeeIcon } from "@heroicons/react/24/solid";
import { stringUtility } from "@repo/utilities/string.utility.ts";
import { Selector } from "@repo/ui/index.tsx";
import useRoutesHandler from "@/Hooks/useRouteHandler";
import { Controller, useForm } from "react-hook-form";

interface IPremiumLineChartProps {
  className?: string;
}

const PremiumLineChart = ({ className }: IPremiumLineChartProps) => {
  const {
    services: { getTotalPremiumCountService },
  } = usePartnerCertificates({
    enableQuery: false,
    enableCountQuery: true,
  });
  const formMethods = useForm();
  const { navigateTo } = useRoutesHandler();

  const YEARS = [
    { id: 1, code: 2024, name: "2024" },
    { id: 2, code: 2025, name: "2025" },
    { id: 3, code: 2026, name: "2026" },
  ];
  const totalPremiumCount: number =
    getTotalPremiumCountService?.data?.data?.reduce(
      (acc: number, data: { total_premium: string }) =>
        acc +
        Number(stringUtility.handleAmount(Number(data.total_premium), "res")),
      0
    );

  const premiumData =
    getTotalPremiumCountService?.data?.data?.map((item: any) => ({
      month: item.month,
      premium: Number(item.total_premium),
    })) || [];

  // CustomTooltip
  const TotalPremiumTooltip = ({ payload }: { payload?: Record<any, any> }) => {
    const payloadValue = payload?.[0]?.payload || {};
    return (
      <div className="bg-white text-xs font-semibold text-body/80 p-2 border border-extraLightGray/80 rounded-md">
        <p>
          <span>Month:</span>{" "}
          <span className="text-primary">
            {MONTHS[payloadValue?.month - 1]}
          </span>
        </p>
        <p>
          <span>Premium:</span>{" "}
          <span className="text-primary">
            &#8377;{" "}
            {stringUtility.formatNumberToIndianScale(
              +stringUtility?.handleAmount(Number(payloadValue.premium), "res")
            )}
          </span>
        </p>
      </div>
    );
  };

  const CustomizedYAxisTick = ({ x, y, payload }: any) => {
    return (
      <g transform={`translate(${x},${y + 4})`}>
        <text
          textAnchor="middle"
          className="fill-body font-medium text-[0.7rem]"
        >
          {MONTHS[payload.value - 1]}
        </text>
      </g>
    );
  };

  if (
    getTotalPremiumCountService?.isLoading &&
    !getTotalPremiumCountService?.data
  )
    return (
      <div className="flex-1 h-full rounded-lg bg-extraLightGray animate-pulse" />
    );
  const date = new Date();
  const getCurrentYear = date.getFullYear();
  const currentYearOption = YEARS.find((year) => year.code === getCurrentYear);

  return (
    <ChartContainer
      headingComp={
        <div className="flex items-center gap-2 w-full">
          <h3 className="font-semibold w-[35%]">Total Premium Sales</h3>
          <section className="flex items-center gap-1 justify-between w-full">
            <div className="flex">
              <CurrencyRupeeIcon className="size-7 text-primary/90" />
              <h2 className="font-bold text-lg text-primary">
                {stringUtility.formatNumberToIndianScale(totalPremiumCount)}
              </h2>
            </div>
            <div className="w-44">
              <Controller
                name="years"
                control={formMethods.control}
                defaultValue={currentYearOption}
                render={({ field: { value, onChange } }) => (
                  <Selector
                    value={value}
                    onChange={(value) => {
                      navigateTo({
                        search: {
                          year: value?.code,
                        },
                      });
                      onChange(value);
                    }}
                    options={YEARS || []}
                    placeholder="Select"
                    textEllipsis={12}
                    className="border-none bg-primary/10 h-8"
                  />
                )}
              />
            </div>
          </section>
        </div>
      }
      className={`h-full ${className}`}
    >
      <div className="h-full pb-10">
        <AreaChartGraph
          data={premiumData}
          yKeyName="premium"
          xKeyName="month"
          tooltipComp={<TotalPremiumTooltip />}
          CustomizedXAxisTickComp={<CustomizedYAxisTick />}
        />
      </div>
    </ChartContainer>
  );
};

export default PremiumLineChart;
